/* ───── Endeks Bilgileri ───── */

export interface IndexMeta {
  /** Türkçe gösterim adı */
  name: string
  /** İngilizce gösterim adı. Verilmezse `name` kullanılır (özel isimler çevrilmez). */
  nameEn?: string
  /** Türkçe açıklama */
  description: string
  /** İngilizce açıklama — ZORUNLU. Eksikse İngilizce sayfada Türkçe metin sızar. */
  descriptionEn: string
  symbol: string
  market: 'us' | 'eu' | 'jp' | 'hk' | 'kr' | 'in' | 'apac' | 'mea' | 'americas' | 'commodity'
}

/**
 * /piyasa/{id} (TR) ve /market/{id} (EN) sayfalarının metadata kaynağı.
 *
 * BU LİSTE BACKEND'İN SERVİS ETTİĞİ id KÜMESİYLE BİREBİR AYNI OLMALIDIR.
 * (Ağustos 2026 denetimi) Eskiden 13 id eşleşmiyordu: backend `cac40`, `ftse100`,
 * `ibex35`, `gold`, `silver`, `brent`, `wti`... servis ederken frontend'de `cac`,
 * `ftse`, `ibex` yazıyordu ve emtialar hiç yoktu. Eşleşmeyen id'lerde `info`
 * sunucuda tanımsız kalıyor, bu yüzden:
 *   • sayfada HİÇ <h1> render edilmiyordu (28 sayfanın 15'i H1'siz),
 *   • başlık "GOLD Borsa Detayı" gibi jenerik kalıyordu.
 * Ayrıca açıklamalar yalnızca Türkçeydi; İngilizce sayfada "(Nasdaq 100 Endeksi)"
 * yazıyordu. Her iki dil de artık zorunlu.
 *
 * NOT: BIST/Türkiye endeksleri (XU100/XU030/XBANK...) bilinçli olarak yok.
 */
export const indexInfo: Record<string, IndexMeta> = {
  // ── ABD ──
  ndx:     { name: 'NASDAQ 100',   description: 'Nasdaq 100 Endeksi',        descriptionEn: 'Nasdaq 100 Index',              symbol: 'NDX',     market: 'us' },
  dji:     { name: 'Dow Jones',    description: 'Dow Jones Sanayi Endeksi',  descriptionEn: 'Dow Jones Industrial Average',  symbol: 'DJI',     market: 'us' },
  rut:     { name: 'Russell 2000', description: 'Russell 2000 Endeksi',      descriptionEn: 'Russell 2000 Index',            symbol: 'RUT',     market: 'us' },

  // ── Amerika Kıtası ──
  bvsp:    { name: 'Bovespa',      description: 'Brezilya Bovespa Endeksi',  descriptionEn: 'Brazil Bovespa Index',          symbol: 'BVSP',    market: 'americas' },
  gsptse:  { name: 'S&P/TSX',      description: 'Kanada S&P/TSX Endeksi',    descriptionEn: 'Canada S&P/TSX Composite Index', symbol: 'GSPTSE', market: 'americas' },

  // ── Avrupa ──
  dax:     { name: 'DAX',          description: 'Almanya DAX Endeksi',       descriptionEn: 'Germany DAX Index',             symbol: 'DAX',     market: 'eu' },
  cac40:   { name: 'CAC 40',       description: 'Fransa CAC 40 Endeksi',     descriptionEn: 'France CAC 40 Index',           symbol: 'CAC40',   market: 'eu' },
  ftse100: { name: 'FTSE 100',     description: 'İngiltere FTSE 100 Endeksi', descriptionEn: 'UK FTSE 100 Index',            symbol: 'FTSE100', market: 'eu' },
  ftsemib: { name: 'FTSE MIB',     description: 'İtalya FTSE MIB Endeksi',   descriptionEn: 'Italy FTSE MIB Index',          symbol: 'FTSEMIB', market: 'eu' },
  ibex35:  { name: 'IBEX 35',      description: 'İspanya IBEX 35 Endeksi',   descriptionEn: 'Spain IBEX 35 Index',           symbol: 'IBEX35',  market: 'eu' },

  // ── Asya-Pasifik ──
  n225:    { name: 'Nikkei 225',   description: 'Japonya Nikkei 225 Endeksi', descriptionEn: 'Japan Nikkei 225 Index',       symbol: 'N225',    market: 'jp' },
  hsi:     { name: 'Hang Seng',    description: 'Hong Kong Hang Seng Endeksi', descriptionEn: 'Hong Kong Hang Seng Index',   symbol: 'HSI',     market: 'hk' },
  ssec:    { name: 'Shanghai Composite', description: 'Çin Şanghay Bileşik Endeksi', descriptionEn: 'China Shanghai Composite Index', symbol: 'SSEC', market: 'hk' },
  ks11:    { name: 'KOSPI',        description: 'Güney Kore KOSPI Endeksi',  descriptionEn: 'South Korea KOSPI Index',       symbol: 'KS11',    market: 'kr' },
  twii:    { name: 'TAIEX',        description: 'Tayvan TAIEX Endeksi',      descriptionEn: 'Taiwan TAIEX Index',            symbol: 'TWII',    market: 'apac' },
  jkse:    { name: 'IDX Composite', description: 'Endonezya IDX Bileşik Endeksi', descriptionEn: 'Indonesia IDX Composite Index', symbol: 'JKSE', market: 'apac' },
  axjo:    { name: 'S&P/ASX 200',  description: 'Avustralya S&P/ASX 200 Endeksi', descriptionEn: 'Australia S&P/ASX 200 Index', symbol: 'AXJO', market: 'apac' },
  nsei:    { name: 'NIFTY 50',     description: 'Hindistan NIFTY 50 Endeksi', descriptionEn: 'India NIFTY 50 Index',         symbol: 'NSEI',    market: 'in' },
  sensex:  { name: 'BSE Sensex',   description: 'Hindistan BSE Sensex Endeksi', descriptionEn: 'India BSE Sensex Index',     symbol: 'SENSEX',  market: 'in' },

  // ── Ortadoğu & Afrika ──
  ta35:    { name: 'TA-35',        description: 'İsrail TA-35 Endeksi',      descriptionEn: 'Israel TA-35 Index',            symbol: 'TA35',    market: 'mea' },
  tasi:    { name: 'Tadawul TASI', description: 'Suudi Arabistan Tadawul Endeksi', descriptionEn: 'Saudi Arabia Tadawul All Share Index', symbol: 'TASI', market: 'mea' },

  // ── Emtia ──
  gold:     { name: 'Altın',      nameEn: 'Gold',            description: 'Altın Ons Fiyatı (XAU/USD)',   descriptionEn: 'Gold Spot Price (XAU/USD)',    symbol: 'GOLD',     market: 'commodity' },
  silver:   { name: 'Gümüş',      nameEn: 'Silver',          description: 'Gümüş Ons Fiyatı (XAG/USD)',   descriptionEn: 'Silver Spot Price (XAG/USD)',  symbol: 'SILVER',   market: 'commodity' },
  platinum: { name: 'Platin',     nameEn: 'Platinum',        description: 'Platin Ons Fiyatı (XPT/USD)',  descriptionEn: 'Platinum Spot Price (XPT/USD)', symbol: 'PLATINUM', market: 'commodity' },
  copper:   { name: 'Bakır',      nameEn: 'Copper',          description: 'Bakır Vadeli Fiyatı',          descriptionEn: 'Copper Futures Price',         symbol: 'COPPER',   market: 'commodity' },
  brent:    { name: 'Brent Petrol', nameEn: 'Brent Crude',   description: 'Brent Ham Petrol Fiyatı',      descriptionEn: 'Brent Crude Oil Price',        symbol: 'BRENT',    market: 'commodity' },
  wti:      { name: 'WTI Petrol', nameEn: 'WTI Crude',       description: 'WTI Ham Petrol Fiyatı',        descriptionEn: 'WTI Crude Oil Price',          symbol: 'WTI',      market: 'commodity' },
  gautry:   { name: 'Gram Altın', nameEn: 'Gram Gold (TRY)', description: 'Gram Altın Fiyatı (TRY)',      descriptionEn: 'Gram Gold Price in Turkish Lira', symbol: 'GAUTRY', market: 'commodity' },
}

export interface StockEntry {
  symbol: string       // Yahoo Finance sembolü (ör: AAPL)
  name: string         // Tam şirket adı
  shortName: string    // Kısa hisse kodu
}

/* ───── Aracı Kurum / Yatırım Platformları ───── */

export interface Broker {
  id: string
  name: string
  /**
   * Komisyon oranı (yüzde değeri). null = komisyonsuz.
   *
   * Ağustos 2026 denetimi: eskiden hazır Türkçe METİN tutuluyordu ('%0,150',
   * '₺0 komisyon') ve İngilizce sayfada aynen basılıyordu — İngilizce'de doğru
   * biçim '0.150%' olmalı. Sayı olarak tutulup dile göre biçimlendiriliyor.
   */
  commissionPct: number | null
  logo: string
  logoBg?: string
}

/** Komisyonu sayfanın diline göre biçimlendirir (TR: %0,15 — EN: 0.15%). */
export function formatCommission(pct: number | null, locale: string): string {
  const isEn = locale === 'en'
  if (pct === null) return isEn ? '₺0 commission' : '₺0 komisyon'
  const n = new Intl.NumberFormat(isEn ? 'en-US' : 'tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(pct)
  return isEn ? `${n}%` : `%${n}`
}

export const brokers: Broker[] = [
  { id: 'midas',     name: 'Midas',              commissionPct: null       , logo: '/midas-logo.png' },
  { id: 'garanti',   name: 'Garanti BBVA',       commissionPct: 0.150,      logo: '/garanti-logo.png' },
  { id: 'istefon',   name: 'İşTeFon',            commissionPct: 0.188,      logo: '/istefon-logo.png' },
  { id: 'akbank',    name: 'Ak Yatırım',         commissionPct: 0.170,      logo: '/akbank-logo.png' },
  { id: 'yapikredi', name: 'Yapı Kredi Yatırım', commissionPct: 0.150,      logo: '/brokers/yapikredi-yatirim-logo.svg', logoBg: '#4A2D82' },
  { id: 'deniz',     name: 'Deniz Yatırım',      commissionPct: 0.150,      logo: '/brokers/deniz-yatirim-logo.svg',     logoBg: '#005073' },
  { id: 'teb',       name: 'TEB Yatırım',        commissionPct: 0.175,      logo: '/brokers/teb-yatirim-logo.webp' },
  { id: 'ziraat',    name: 'Ziraat Yatırım',     commissionPct: 0.180,      logo: '/brokers/ziraat-yatirim-logo.png' },
  { id: 'vakif',     name: 'Vakıf Yatırım',      commissionPct: 0.175,      logo: '/brokers/vakif-yatirim-logo.png' },
  { id: 'halk',      name: 'Halk Yatırım',       commissionPct: 0.180,      logo: '/brokers/halk-yatirim-logo.svg',      logoBg: '#00853E' },
  { id: 'qnb',       name: 'QNB Finans Yatırım', commissionPct: 0.175,      logo: '/brokers/qnb-invest-logo.png' },
  { id: 'oyak',      name: 'Oyak Yatırım',       commissionPct: 0.188,      logo: '/brokers/oyak-yatirim-logo.png' },
  { id: 'gedik',     name: 'Gedik Yatırım',      commissionPct: 0.188,      logo: '/brokers/gedik-yatirim-logo.png',     logoBg: '#1A2B6B' },
  { id: 'tera',      name: 'Tera Yatırım',       commissionPct: 0.200,      logo: '/brokers/tera-yatirim-logo.svg' },
  { id: 'gcm',       name: 'GCM Yatırım',        commissionPct: 0.200,      logo: '/brokers/gcm-yatirim-logo.png' },
  { id: 'unlu',      name: 'ÜNLÜ Menkul',        commissionPct: 0.200,      logo: '/brokers/unlu-menkul-logo.jpg' },
  { id: 'tacirler',  name: 'Tacirler Yatırım',   commissionPct: 0.200,      logo: '/brokers/tacirler-yatirim-logo.svg',  logoBg: '#1B2D6E' },
  { id: 'integral',  name: 'İntegral Yatırım',   commissionPct: 0.200,      logo: '/brokers/integral-yatirim-logo.webp' },
  { id: 'info',      name: 'İnfo Yatırım',       commissionPct: 0.200,      logo: '/brokers/info-yatirim-logo.svg' },
  { id: 'seker',     name: 'Şeker Yatırım',      commissionPct: 0.200,      logo: '/brokers/seker-yatirim-logo.svg' },
  { id: 'global',    name: 'Global Menkul',      commissionPct: 0.200,      logo: '/brokers/global-menkul-logo.png' },
  { id: 'ahlatci',   name: 'Ahlatçı Yatırım',   commissionPct: 0.188,      logo: '/brokers/ahlatci-yatirim-logo.svg' },
  { id: 'piramit',   name: 'Piramit Menkul',     commissionPct: 0.200,      logo: '/brokers/piramit-menkul-logo.png' },
  { id: 'burgan',    name: 'Burgan Yatırım',     commissionPct: 0.200,      logo: '/brokers/burgan-yatirim-logo.svg' },
  { id: 'hedef',     name: 'Hedef Yatırım',      commissionPct: 0.200,      logo: '/brokers/hedef-yatirim-logo.svg' },
]

// US/EU/Asya hisselerinde de aynı broker listesi gösterilir (tek kaynak: brokers).
// Böylece /araci-kurumlar sayfasında olmayan bir broker hisse detayında gözükmez.
export const usBrokers: Broker[] = brokers
export const euBrokers: Broker[] = brokers
export const asiaBrokers: Broker[] = brokers

// Türkiye'den lisanslı kripto borsaları (TL para yatırma + KYC ile)
export const cryptoBrokersTr: Broker[] = [
  { id: 'btcturk',     name: 'BtcTurk',     commissionPct: 0.18,  logo: '/crypto-brokers/btcturk.svg',  logoBg: '#0096D8' },
  { id: 'paribu',      name: 'Paribu',      commissionPct: 0.15,  logo: '/crypto-brokers/paribu.svg',   logoBg: '#FF6B00' },
  { id: 'binance-tr',  name: 'Binance TR',  commissionPct: 0.10,  logo: '/crypto-brokers/binance.svg',  logoBg: '#F0B90B' },
  { id: 'bitexen',     name: 'Bitexen',     commissionPct: 0.15,  logo: '/crypto-brokers/bitexen.png',  logoBg: '#1F2937' },
]

// Global kripto borsaları (geniş coin yelpazesi, tier-1 likidite)
export const cryptoBrokersGlobal: Broker[] = [
  { id: 'binance',     name: 'Binance',     commissionPct: 0.10,  logo: '/crypto-brokers/binance.svg',  logoBg: '#F0B90B' },
  { id: 'coinbase',    name: 'Coinbase',    commissionPct: 0.40,  logo: '/crypto-brokers/coinbase.svg', logoBg: '#0052FF' },
  { id: 'kraken',      name: 'Kraken',      commissionPct: 0.16,  logo: '/crypto-brokers/kraken.svg',   logoBg: '#5741D9' },
  { id: 'okx',         name: 'OKX',         commissionPct: 0.08,  logo: '/crypto-brokers/okx.svg',      logoBg: '#000000' },
]

// Birleşik liste: kullanıcı için tek bölüm. TR ilk (yerel öncelik), sonra Global.
export const cryptoBrokersAll: Broker[] = [
  ...cryptoBrokersTr,
  ...cryptoBrokersGlobal,
]
